import { isDateString, sortStops } from "./domain";
import type { ItineraryStop, Trip } from "./types";

export const TRIP_EXPORT_VERSION = 2;

export interface TripJsonExport {
  format: "roam-trip";
  version: typeof TRIP_EXPORT_VERSION;
  exportedAt: string;
  trip: Trip;
}

export interface TripJsonExportOptions {
  exportedAt?: string;
}

/** Serialize the complete v2 trip in a self-describing, versioned envelope. */
export function exportTripJson(trip: Trip, options: TripJsonExportOptions = {}): string {
  const payload: TripJsonExport = {
    format: "roam-trip",
    version: TRIP_EXPORT_VERSION,
    exportedAt: options.exportedAt ?? new Date().toISOString(),
    trip,
  };
  return JSON.stringify(payload, null, 2);
}

/** Compatibility name for consumers that call serialization directly. */
export const serializeTrip = exportTripJson;

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function htmlText(value: unknown): string {
  return escapeHtml(value).replaceAll("\n", "<br />");
}

function renderStop(stop: ItineraryStop): string {
  const schedule = stop.startTime ? `${escapeHtml(stop.startTime)} · ${escapeHtml(stop.durationMinutes)} min` : `${escapeHtml(stop.durationMinutes)} min`;
  const details = [
    `<span class="stop-schedule">${schedule}</span>`,
    stop.address ? `<span class="stop-address">${escapeHtml(stop.address)}</span>` : "",
    stop.category ? `<span class="stop-category">${escapeHtml(stop.category)}</span>` : "",
    stop.notes ? `<p class="stop-notes">${htmlText(stop.notes)}</p>` : "",
  ].filter(Boolean).join(" ");
  return `<li class="itinerary-stop"><h3>${escapeHtml(stop.name)}</h3><div class="stop-meta">${details}</div></li>`;
}

function renderDay(trip: Trip, day: Trip["days"][number]): string {
  const stops = sortStops(trip.stops.filter((stop) => stop.dayId === day.id));
  const dateLabel = day.date ? ` <time datetime="${escapeHtml(day.date)}">${escapeHtml(day.date)}</time>` : "";
  return `<section class="itinerary-day" aria-labelledby="day-${escapeHtml(day.id)}"><h2 id="day-${escapeHtml(day.id)}">Day ${escapeHtml(day.dayIndex)}${dateLabel}</h2>${stops.length ? `<ol>${stops.map(renderStop).join("")}</ol>` : "<p>No stops planned.</p>"}</section>`;
}

/** Render a standalone semantic HTML itinerary with an explicit route limitation. */
export function exportTripHtml(trip: Trip): string {
  const days = trip.days.map((day) => renderDay(trip, day)).join("\n");
  const unscheduled = sortStops(trip.stops.filter((stop) => stop.dayId === null));
  const unscheduledSection = `<section class="itinerary-unscheduled" aria-labelledby="unscheduled-heading"><h2 id="unscheduled-heading">Unscheduled</h2>${unscheduled.length ? `<ol>${unscheduled.map(renderStop).join("")}</ol>` : "<p>No unscheduled stops.</p>"}</section>`;
  const dateRange = trip.startDate && trip.endDate ? `<p class="trip-dates"><time datetime="${escapeHtml(trip.startDate)}">${escapeHtml(trip.startDate)}</time> to <time datetime="${escapeHtml(trip.endDate)}">${escapeHtml(trip.endDate)}</time></p>` : "";
  return `<!doctype html>
<html lang="en">
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>${escapeHtml(trip.name)} itinerary</title></head>
<body><main>
<header><h1>${escapeHtml(trip.name)}</h1><p class="destination">${escapeHtml(trip.destination)}</p>${dateRange}<p class="route-disclosure"><strong>Route note:</strong> Distances are straight-line estimates, not turn-by-turn directions.</p></header>
<div class="itinerary-days">${days}</div>
${unscheduledSection}
${trip.notes ? `<section class="trip-notes" aria-labelledby="trip-notes-heading"><h2 id="trip-notes-heading">Trip notes</h2><p>${htmlText(trip.notes)}</p></section>` : ""}
</main></body></html>`;
}

function icsEscape(value: string): string {
  return value.replaceAll("\\", "\\\\").replaceAll(";", "\\;").replaceAll(",", "\\,").replaceAll("\r\n", "\\n").replaceAll("\n", "\\n").replaceAll("\r", "\\n");
}

function icsDate(date: string): string {
  return date.replaceAll("-", "");
}

function nextDate(date: string): string {
  const parsed = new Date(`${date}T00:00:00.000Z`);
  parsed.setUTCDate(parsed.getUTCDate() + 1);
  return parsed.toISOString().slice(0, 10);
}

function icsDateTime(date: string, startTime: string, durationMinutes: number): { start: string; end: string } {
  const start = new Date(`${date}T${startTime}:00.000Z`);
  const end = new Date(start.getTime() + durationMinutes * 60_000);
  const format = (value: Date) => value.toISOString().replaceAll("-", "").replaceAll(":", "").replace(".000Z", "Z");
  return { start: format(start), end: format(end) };
}

function stamp(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date().toISOString().replaceAll("-", "").replaceAll(":", "").replace(".000Z", "Z") : parsed.toISOString().replaceAll("-", "").replaceAll(":", "").replace(".000Z", "Z");
}

export interface IcsExportAvailable {
  available: true;
  content: string;
  filename: string;
}

export interface IcsExportUnavailable {
  available: false;
  reason: "undated";
  message: string;
}

export type IcsExportResult = IcsExportAvailable | IcsExportUnavailable;

/** RFC5545-ish calendar export. Undated trips return a typed unavailable result. */
export function exportTripIcs(trip: Trip): IcsExportResult {
  const datedDays = trip.days.filter((day) => day.date && isDateString(day.date));
  if (!datedDays.length) {
    return { available: false, reason: "undated", message: "Calendar export is unavailable because this trip has no dates." };
  }

  const events: string[] = [];
  for (const day of datedDays) {
    const dayDate = day.date!;
    for (const stop of sortStops(trip.stops.filter((candidate) => candidate.dayId === day.id || (candidate.dayId === null && candidate.date === dayDate)))) {
      const summary = `${trip.name}: ${stop.name}`;
      const lines = [
        "BEGIN:VEVENT",
        `UID:${icsEscape(`${trip.id}-${stop.id}@roam.codepawl.com`)}`,
        `DTSTAMP:${stamp(trip.updatedAt)}`,
      ];
      if (stop.startTime && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(stop.startTime)) {
        const dateTime = icsDateTime(dayDate, stop.startTime, stop.durationMinutes);
        lines.push(`DTSTART:${dateTime.start}`, `DTEND:${dateTime.end}`);
      } else {
        lines.push(`DTSTART;VALUE=DATE:${icsDate(dayDate)}`, `DTEND;VALUE=DATE:${icsDate(nextDate(dayDate))}`);
      }
      lines.push(`SUMMARY:${icsEscape(summary)}`);
      if (stop.address) lines.push(`LOCATION:${icsEscape(stop.address)}`);
      const description = [stop.notes, stop.transport ? `Transport: ${stop.transport}` : ""].filter(Boolean).join("\n");
      if (description) lines.push(`DESCRIPTION:${icsEscape(description)}`);
      lines.push("END:VEVENT");
      events.push(lines.join("\r\n"));
    }
  }

  const content = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//CodePawl//Roam//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    ...events,
    "END:VCALENDAR",
    "",
  ].join("\r\n");
  return { available: true, content, filename: `${trip.name.trim().toLocaleLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "roam-trip"}.ics` };
}

/** String-only convenience wrapper with a documented error for undated trips. */
export function toIcs(trip: Trip): string {
  const result = exportTripIcs(trip);
  if (!result.available) throw new Error(result.message);
  return result.content;
}

export const exportTripICS = exportTripIcs;
